import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal, message } from "antd";
import type { Dayjs } from "dayjs";
import { useSelector } from "react-redux";
import { Edit2, Zap } from "react-feather";

import styles from "./BetTable.module.css";
import BetsFilters from "./BetsFilters";
import { handleExpiredTokenError } from "../../../../utils/ErrorUtils";
import { formatLargeNumbers } from "../../../../utils/NumberFormatter";

import {
  AGENT_ACTIONS,
  BET_STATUS,
  KPI_CARD_BACKGROUND,
} from "../../../../utils/Constants";
import KpiCard from "../../../CommonComponents/Dashboard/KpiCard/KpiCard";
import CustomDatePicker from "../../../CommonComponents/CustomDatePicker/CustomDatePicker";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { parseTimestampString,
  getAdminNow,
} from "../../../../utils/dateService";
import SettleBetPopup from "./SettleBetPopup/SettleBetPopup";
import EditBetPopup from "./EditBetPopup/EditBetPopup";
import {
  isGroupBet,
  processBets,
  processParlayBets,
} from "../../../../utils/BetTableUtils";
import { BET_TYPE_LABELS, betTypeLabel } from "../../../../utils/betTypeLabel";
import {
  DataTable as DataTableUntyped,
  StatusBadge,
  useExternalTableFilters,
  filterQueryUtils,
  Button,
} from "../../../shared";
import type { StatusBadgeTone } from "../../../shared";
import AppContext from "../../../../context/AppContext";

import { isTruthyFlag } from "../../../../utils/UserUtils";
import { useTriveltaProduct } from "../../../../hooks/useTriveltaProduct";
import { TRIVELTA_PRODUCTS } from "../../../../utils/products";
import { useGetUserBets } from "../../../../queries/user-manager/useGetUserBets";
import { useGetBetFilterDropdowns } from "../../../../queries/user-manager/useGetBetFilterDropdowns";
import { useBetGamePassSubmissions } from "../../../../queries/game-pass/useBetGamePassSubmissions";
import {
  moneyTypeLabel,
  relabelMoneyTypeOptions,
} from "../../../../utils/MoneyTypeLabels";
import type { BetApiRecord, BetTableRow } from "../../../../types/bets";

const { resolveDateRangeParams } = filterQueryUtils;

// DataTable is a JS module without types; cast to an FC typed for the props this component uses.
// Mirrors the pattern in AllManualActions.tsx (TypedDataTable).
type BetTableDataTableProps = {
  columns: Array<Record<string, unknown>>;
  dataSource: BetTableRow[];
  scrollPreset?: string;
  loading?: boolean;
  rowKey?: (record: BetTableRow) => string | undefined;
  expandable?: {
    expandedRowKeys: string[];
    onExpand: (expanded: boolean, record: BetTableRow) => void;
  };
  rowClassName?: (record: BetTableRow) => string;
  pagination:
    | false
    | {
        current: number;
        pageSize: number;
        total: number;
        onChange: (page: number, size: number) => void;
        showSizeChanger?: boolean;
        pageSizeOptions?: readonly string[] | string[];
        showQuickJumper?: boolean;
        hideOnSinglePage?: boolean;
        itemRender?: (
          page: number,
          type: "page" | "prev" | "next" | "jump-prev" | "jump-next",
          originalElement: React.ReactNode,
        ) => React.ReactNode;
      };
};
const TypedDataTable =
  DataTableUntyped as unknown as React.FC<BetTableDataTableProps>;

const BET_STATUS_TONE: Record<string, StatusBadgeTone> = {
  won: "success",
  lost: "danger",
  pending: "warning",
  open: "warning",
  unsettled: "neutral",
  cancelled: "neutral",
  canceled: "neutral",
  pushed: "info",
  push: "info",
  void: "neutral",
  voided: "neutral",
  "cashed-out": "info",
};

const getBetStatusTone = (text: string): StatusBadgeTone => {
  const key = text?.split(" ").join("-").toLowerCase();
  return BET_STATUS_TONE[key] ?? "neutral";
};

type UserData = { user_id?: string };

interface BetTableProps {
  userData?: UserData | null;
  currencySymbol?: string;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _adminData?: unknown;
}

type DateRange = [Dayjs, Dayjs] | null;

type Summary = {
  total_wager: number;
  total_payout: number;
  total_net_revenue: number;
};

const EMPTY_SUMMARY: Summary = {
  total_wager: 0,
  total_payout: 0,
  total_net_revenue: 0,
};

const DEFAULT_PAGE_SIZE = 20;
// Ant Design's built-in pagination expects `pageSizeOptions` as strings.
const PAGE_SIZE_OPTIONS: readonly string[] = ["10", "20", "50", "100"];

const BetTable: React.FC<BetTableProps> = ({ userData, currencySymbol }) => {
  const { appConfig } = useSelector(
    (state: {
      config: { appConfig?: { app_currency_config?: { odds_type?: string } } };
    }) => state.config,
  );
  const { adminActions } = useContext(AppContext) as {
    adminActions?: string[];
  };
  const canSettleBet = adminActions?.includes(AGENT_ACTIONS?.SETTLE_BET);
  // Retained for future odds-format toggling; currently unused but read from config.
  void appConfig?.app_currency_config?.odds_type;
  const navigate = useNavigate();
  const location = useLocation();

  const todayRange: [Dayjs, Dayjs] = useMemo(
    () => [getAdminNow().startOf("day"), getAdminNow().endOf("day")],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [betsDates, setBetsDates] = useState<DateRange>(null);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [cursorsByPage, setCursorsByPage] = useState<
    Record<number, string | Record<string, unknown> | null>
  >({ 1: null });
  const [cachedTotalsJson, setCachedTotalsJson] = useState<string | null>(null);
  const [summary, setSummary] = useState<Summary>(EMPTY_SUMMARY);
  const [isSettleBetModalOpen, setIsSettleBetModalOpen] = useState(false);
  const [isEditBetPopupVisible, setIsEditBetPopupVisible] = useState(false);
  const [selectedBetData, setSelectedBetData] = useState<BetTableRow | {}>({});
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const {
    filters,
    normalizedFilters,
    setFilter,
    resetFilters,
    hasActiveFilters,
  } = useExternalTableFilters({
    searchValue: "",
    bet_types: [] as string[],
    status: [] as string[],
    money_type: "",
  }) as unknown as {
    filters: {
      searchValue: string;
      bet_types: string[];
      status: string[];
      money_type: string;
    };
    normalizedFilters: {
      searchValue: string;
      bet_types: string[];
      status: string[];
      money_type: string;
    };
    setFilter: (key: string, value: string | string[]) => void;
    resetFilters: () => void;
    hasActiveFilters: () => boolean;
  };

  const [appliedStatus, setAppliedStatus] = useState<string[]>([]);
  const [appliedMoneyType, setAppliedMoneyType] = useState<string>("");
  const [appliedBetTypes, setAppliedBetTypes] = useState<string[]>([]);
  // Exact bet-id lookup is a backend override (PAMENG-1867): while set, the
  // list query ignores bet_types/status/money_type/date range server-side, so
  // the UI clears those filters when a search fires (see handleSearchPressed).
  const [appliedBetId, setAppliedBetId] = useState<string>("");
  const isBetIdSearchActive = Boolean(appliedBetId);

  // FE-2300: the "Game Pass" submission column rides the Game Pass product
  // gate — the same product + legacy-fallback pair that shows/hides the Game
  // Pass tab (PAMENG-1820), so the column appears iff the tab does. While the
  // product is off (or still resolving), the column is hidden and no
  // enrollments/progress calls fire.
  const { isActive: isGamePassProductActive } = useTriveltaProduct(
    TRIVELTA_PRODUCTS.GAME_PASS,
    "gamePass",
  );
  const showGamePassColumn = isGamePassProductActive === true;
  const {
    taskReferencesByBetId,
    isLoading: isGamePassSubmissionsLoading,
    refresh: refreshGamePassSubmissions,
  } = useBetGamePassSubmissions(userData?.user_id, {
    enabled: showGamePassColumn,
  });

  const { data: filterDropdowns } = useGetBetFilterDropdowns("sportsbook");
  const statusOptions = filterDropdowns?.statuses ?? [];
  const betTypeOptions = filterDropdowns?.bet_types ?? [];
  const currencyOptions = useMemo(
    () => relabelMoneyTypeOptions(filterDropdowns?.currencies ?? []),
    [filterDropdowns?.currencies],
  );

  const effectiveDateRange = betsDates ?? todayRange;
  const allTime = betsDates === null;
  const { startDate, endDate } = resolveDateRangeParams({
    queryString: effectiveDateRange,
    isQueryString: false,
    allTime,
  });

  const queryParams = userData?.user_id
    ? {
        user_id: userData.user_id,
        page,
        page_size: pageSize,
        start_date: startDate,
        end_date: endDate,
        last_evaluated_key: cursorsByPage[page] ?? null,
        ...(page > 1 && cachedTotalsJson ? { totals: cachedTotalsJson } : {}),
        ...(appliedStatus.length > 0 ? { status: appliedStatus } : {}),
        ...(appliedMoneyType ? { money_type: appliedMoneyType } : {}),
        ...(appliedBetTypes.length > 0 ? { bet_types: appliedBetTypes } : {}),
        ...(appliedBetId ? { bet_id: appliedBetId } : {}),
      }
    : null;

  const query = useGetUserBets(queryParams);

  const isKpisLoading = page === 1 && query.isFetching;

  // Sync cursor map when a page response arrives.
  useEffect(() => {
    const nextKey = query.data?.last_evaluated_key;
    if (nextKey === undefined) return;
    setCursorsByPage((prev) => {
      const existing = prev[page + 1];
      const serialize = (k: unknown) =>
        k === null || k === undefined
          ? ""
          : typeof k === "string"
            ? k
            : JSON.stringify(k);
      if (serialize(existing) === serialize(nextKey)) return prev;
      return { ...prev, [page + 1]: nextKey ?? null };
    });
  }, [query.data, page]);

  // Update KPIs only on the first page of a (possibly new) query.
  useEffect(() => {
    const totals = query.data?.totals;
    if (!totals || page !== 1) return;
    const totalWager = totals.total_wagers ?? 0;
    const totalPayout = totals.total_payouts ?? 0;
    setSummary({
      total_wager: totalWager,
      total_payout: totalPayout,
      total_net_revenue: totalWager - totalPayout,
    });
    setCachedTotalsJson(JSON.stringify(totals));
  }, [query.data, page]);

  // Surface fetch errors via the shared expired-token handler.
  useEffect(() => {
    if (query.isError && query.error) {
      // axios-like error: handleExpiredTokenError expects (navigate, response)
      const err = query.error as unknown as {
        response?: { data?: { message?: string } };
      };
      if (isBetIdSearchActive) {
        message.error(
          err?.response?.data?.message || AppStrings.ERROR_SEARCHING_BETS,
        );
      }
      handleExpiredTokenError(navigate, err?.response);
    }
  }, [query.isError, query.error, navigate, isBetIdSearchActive]);

  const rawBets: BetApiRecord[] = useMemo(() => {
    const items = query.data?.items;
    if (!items || items.length === 0) return [];
    const sorted = [...items].sort(
      (a, b) =>
        parseFloat(String(b?.created_at)) - parseFloat(String(a?.created_at)),
    );
    const unique = Array.from(
      new Map(sorted.map((bet) => [bet?.bet_id, bet])).values(),
    );
    return unique;
  }, [query.data]);

  const betsToShowInTable: BetTableRow[] = useMemo(() => {
    const processed = processBets(rawBets, isGroupBet);
    return processParlayBets(processed);
  }, [rawBets]);

  const pagination = useMemo(() => {
    return (
      query.data?.pagination ?? {
        current_page: page,
        page_size: 20,
        total_results: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false,
      }
    );
  }, [query.data, page]);

  const isApiRunning = query.isFetching;

  // CustomDatePicker already updates `betsDates` via the `setDates` prop before
  // calling this callback, so here we only reset pagination/KPI state. Exiting
  // bet-id mode too — dates are ignored server-side while an ID search is
  // pinned, so a date change signals intent to go back to the list.
  const handleDateChange = useCallback(() => {
    setCachedTotalsJson(null);
    setAppliedBetId("");
    // Keep the search box in sync with bet-id state — otherwise the input
    // still shows the old ID while the list ignores it (misleading + a footgun
    // if the user presses search again without clearing).
    setFilter("searchValue", "");
    setPage(1);
    setCursorsByPage({ 1: null });
  }, [setFilter]);

  const handleWager = useCallback((record: BetTableRow): string => {
    if (record?.money_type === "") return "";
    return formatLargeNumbers(record?.wager_amount ?? 0, 2);
  }, []);

  const handlePayout = useCallback((record: BetTableRow): string => {
    const payoutNumeric = Number(record?.payout);
    if (record?.payout === "" || Number.isNaN(payoutNumeric)) return "0.00";

    if (record?.payout) {
      return formatLargeNumbers(record.payout, 2);
    }
    if (record?.display_status === BET_STATUS?.LOST) return "0.00";
    if (record?.display_status === BET_STATUS?.CANCELLED) {
      return formatLargeNumbers(record?.wager_amount ?? 0, 2);
    }
    return "-";
  }, []);

  const handleBetType = useCallback((record: BetTableRow): string => {
    // Leg rows of a parlay/round-robin are sub-rows of their parent bet; the
    // bet type is shown on the parent, so legs stay blank.
    if (record?.isChild) return "";
    // Group-of-singles slips are expanded client-side into one row per leg;
    // those leg rows carry no classification block of their own, so fall back
    // to the group label.
    if (record?.is_group_bet) return BET_TYPE_LABELS.group_singles;
    // Classification is backend-owned; the UI only maps it to a label.
    return betTypeLabel(record?.classification);
  }, []);

  const handleSettleBetPressed = useCallback((bet_data: BetTableRow) => {
    setSelectedBetData(bet_data);
    setIsSettleBetModalOpen(true);
  }, []);

  const handleEditBetPressed = useCallback((bet_data: BetTableRow) => {
    setSelectedBetData(bet_data);
    setIsEditBetPopupVisible(true);
  }, []);

  const handleClearFilters = useCallback(() => {
    resetFilters();
    setAppliedStatus([]);
    setAppliedMoneyType("");
    setAppliedBetTypes([]);
    setAppliedBetId("");
    setCachedTotalsJson(null);
    setPage(1);
    setCursorsByPage({ 1: null });
  }, [resetFilters]);

  const handleResetSearch = useCallback(() => {
    setFilter("searchValue", "");
    setAppliedBetId("");
    setCachedTotalsJson(null);
    setPage(1);
    setCursorsByPage({ 1: null });
  }, [setFilter]);

  const { refetch: refetchBets } = query;
  const refreshBets = useCallback(() => {
    setAppliedBetId("");
    // Match the search box to bet-id state so a post-edit refresh doesn't leave
    // a stale ID in the input while the list shows the full results.
    setFilter("searchValue", "");
    setCachedTotalsJson(null);
    setPage(1);
    setCursorsByPage({ 1: null });
    refetchBets();
  }, [refetchBets, setFilter]);

  const handleSearchValueChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFilter("searchValue", event?.target?.value);
    },
    [setFilter],
  );

  const handleBetTypesFilterChange = useCallback(
    (next: string[] | undefined) => {
      setFilter("bet_types", next ?? []);
    },
    [setFilter],
  );

  const handleStatusFilterChange = useCallback(
    (next: string[] | undefined) => {
      setFilter("status", next ?? []);
    },
    [setFilter],
  );

  const handleMoneyTypeFilterChange = useCallback(
    (next: string | undefined) => {
      setFilter("money_type", next ?? "");
    },
    [setFilter],
  );

  const handleApplyFilters = useCallback(() => {
    setAppliedStatus(normalizedFilters.status ?? []);
    setAppliedMoneyType(normalizedFilters.money_type ?? "");
    setAppliedBetTypes(normalizedFilters.bet_types ?? []);
    // Exit bet-id mode so the applied filters actually feed the list query —
    // while `bet_id` is set, the backend ignores every other filter. Also wipe
    // the search input so the user isn't left with a stale ID visible while
    // the filtered list renders.
    setFilter("searchValue", "");
    setAppliedBetId("");
    setCachedTotalsJson(null);
    setPage(1);
    setCursorsByPage({ 1: null });
  }, [
    normalizedFilters.status,
    normalizedFilters.money_type,
    normalizedFilters.bet_types,
    setFilter,
  ]);

  // Pin the list query to a single bet id. The backend ignores
  // bet_types/status/money_type/dates in this mode, so the drafts and applied
  // filters are cleared too — leftover selections would otherwise look like
  // they applied to the result.
  const applyBetIdSearch = useCallback(
    (betId: string) => {
      setFilter("bet_types", []);
      setFilter("status", []);
      setFilter("money_type", "");
      setAppliedStatus([]);
      setAppliedMoneyType("");
      setAppliedBetTypes([]);
      setAppliedBetId(betId);
      setCachedTotalsJson(null);
      setPage(1);
      setCursorsByPage({ 1: null });
      // FE-2330: a bet-id search implies the agent wants the current Game
      // Pass submitted/not-submitted state — refetch it instead of serving
      // the cache from page load.
      refreshGamePassSubmissions();
    },
    [setFilter, refreshGamePassSubmissions],
  );

  const handleSearchPressed = useCallback(() => {
    if (!normalizedFilters.searchValue) return;
    applyBetIdSearch(String(normalizedFilters.searchValue));
  }, [normalizedFilters.searchValue, applyBetIdSearch]);

  // Auto-fire single-bet search when arriving with ?betId from the Users page
  // redirect. Runs once per mount, then strips betId/betType from the URL so a
  // refresh doesn't re-trigger. `betType` is legacy (the V2 bet_id lookup
  // doesn't need it) but is still stripped when present.
  const autoSearchFiredRef = useRef(false);
  useEffect(() => {
    autoSearchFiredRef.current = false;
  }, [userData?.user_id]);
  useEffect(() => {
    if (autoSearchFiredRef.current) return;
    if (!userData?.user_id) return;
    const params = new URLSearchParams(location.search);
    const betId = params.get("betId");
    if (!betId) return;
    autoSearchFiredRef.current = true;

    setFilter("searchValue", betId);
    applyBetIdSearch(betId);

    params.delete("betId");
    params.delete("betType");
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true },
    );
  }, [
    userData?.user_id,
    location.search,
    location.pathname,
    setFilter,
    applyBetIdSearch,
    navigate,
  ]);

  const handlePageSizeChange = useCallback((nextSize: number) => {
    setPageSize(nextSize);
    // Cursor-based pagination assumes a fixed page_size, so any change
    // must invalidate the cached cursors and totals and restart from
    // page 1. The next page's totals will be re-fetched on request.
    setCursorsByPage({ 1: null });
    setCachedTotalsJson(null);
    setPage(1);
  }, []);

  // Ant Design's Table.pagination.onChange also fires when the user
  // switches page size — in that case `nextPage` is coerced back to 1
  // and `nextSize` differs from the current state. Route that through
  // handlePageSizeChange so cursor/cache invariants stay in sync.
  const handlePaginationChange = useCallback(
    (nextPage: number, nextSize: number) => {
      if (nextSize !== pageSize) {
        handlePageSizeChange(nextSize);
        return;
      }
      setPage(nextPage);
    },
    [pageSize, handlePageSizeChange],
  );

  // Shape matches the expected antd/DataTable column contract but kept loose
  // because individual renders accept different text shapes (string, number, undefined).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns = useMemo<Array<Record<string, any>>>(
    () => [
      {
        title: AppStrings.DATE_TIME,
        dataIndex: "created_at",
        key: "created_at",
        width: 200,
        render: (text: string, record: BetTableRow) =>
          text === ""
            ? ""
            : parseTimestampString(record.created_at).format("DD MMMM YYYY, HH:mm:ss"),
      },
      {
        title: AppStrings.EVENT_ID,
        dataIndex: "event_id",
        key: "event_id",
        width: 150,
      },
      {
        title: AppStrings.ID,
        dataIndex: "bet_id",
        key: "bet_id",
        width: 300,
      },
      {
        title: AppStrings.MATCH,
        dataIndex: "title",
        key: "title",
        width: 250,
      },
      {
        title: AppStrings.MARKET,
        dataIndex: "market",
        key: "market",
        width: 250,
      },
      {
        title: AppStrings.SELECTION,
        dataIndex: "selection",
        key: "selection",
        width: 200,
      },
      {
        title: AppStrings.MONEY_TYPE,
        dataIndex: "money_type",
        key: "money_type",
        width: 150,
        render: (text: string) => (text === "" ? "" : moneyTypeLabel(text)),
      },
      {
        title: AppStrings.PRE_MATCH_LIVE,
        dataIndex: "prematch_live",
        key: "prematch_live",
        width: 150,
      },
      {
        title: AppStrings.WAGER,
        dataIndex: "wager",
        key: "wager",
        width: 150,
        sorter: (a: BetTableRow, b: BetTableRow) =>
          Number(a.wager_amount) - Number(b.wager_amount),
        render: (_text: unknown, record: BetTableRow) => handleWager(record),
      },
      {
        title: AppStrings.PAYOUT,
        dataIndex: "payout",
        key: "payout",
        width: 150,
        sorter: (a: BetTableRow, b: BetTableRow) =>
          Number(a.payout) - Number(b.payout),
        render: (_text: unknown, record: BetTableRow) => {
          return record.display_status === "Cashed Out"
            ? formatLargeNumbers(record.cashout_amount ?? 0, 2)
            : handlePayout(record);
        },
      },
      {
        title: AppStrings.STATUS,
        dataIndex: "display_status",
        key: "display_status",
        width: 150,
        render: (text: string) => {
          if (!text) return "—";
          if (text === "-") return text;
          return (
            <StatusBadge tone={getBetStatusTone(text)}>{text}</StatusBadge>
          );
        },
      },
      // FE-2300: whether the bet was submitted toward a Game Pass task
      // ("Submitted: Task 1.2" = challenge 1, second task). Bet-level concept,
      // so parlay leg sub-rows stay blank like the other bet-level columns.
      ...(showGamePassColumn
        ? [
            {
              title: AppStrings.GAME_PASS,
              dataIndex: "bet_id",
              key: "game_pass",
              width: 180,
              render: (text: string, record: BetTableRow) => {
                if (text === "" || record?.isChild) return "";
                // Blank while progress resolves — flashing "Not Submitted"
                // before the data lands would be misleading.
                if (isGamePassSubmissionsLoading) return "";
                const taskReference = taskReferencesByBetId.get(
                  String(record?.bet_id ?? ""),
                );
                return taskReference ? (
                  <StatusBadge tone="success">
                    {`${AppStrings.GAME_PASS_BET_SUBMITTED}: ${taskReference}`}
                  </StatusBadge>
                ) : (
                  <StatusBadge tone="neutral">
                    {AppStrings.GAME_PASS_BET_NOT_SUBMITTED}
                  </StatusBadge>
                );
              },
            },
          ]
        : []),
      {
        title: AppStrings.TYPE,
        dataIndex: "bet_type",
        key: "bet_type",
        width: 120,
        render: (text: string, record: BetTableRow) =>
          text === "" ? "" : handleBetType(record),
      },
      {
        title: AppStrings.ODDS,
        dataIndex: "odds",
        key: "odds",
        width: 160,
        render: (text: string, record: BetTableRow) => {
          if (!record.profit_boost || !record.boosted_odds) {
            return text;
          }
          return (
            <span
              className={styles.profitBoostOdds}
              data-testid="profit-boost-odds"
            >
              {text && (
                <span
                  className={styles.profitBoostOriginalOdds}
                  data-testid="profit-boost-original-odds"
                >
                  {text}
                </span>
              )}
              {record.boosted_odds}
              <Zap
                size={12}
                className={styles.profitBoostIcon}
                data-testid="profit-boost-icon"
              />
            </span>
          );
        },
      },
      {
        title: AppStrings.POTENTIAL_PAYOUT,
        dataIndex: "potential_payout",
        key: "potential_payout",
        width: 180,
        render: (text: string | number | undefined) => {
          if (text === "") return "";
          return text != null ? formatLargeNumbers(text, 2) : "-";
        },
      },
      {
        title: AppStrings.BALANCE,
        dataIndex: "balance",
        key: "balance",
        width: 100,
      },
      {
        title: AppStrings.EDIT,
        dataIndex: "edit",
        key: "edit",
        width: 80,
        fixed: "right" as const,
        render: (text: string, record: BetTableRow) => {
          if (text === "") return "";
          const isRoundRobinOrParlay =
            record.bet_type === "4" ||
            (record.bet_type === "5" && isTruthyFlag(record.is_parlay));
          const disabled =
            isTruthyFlag(record.finalized) ||
            isTruthyFlag(record.cancelled) ||
            !canSettleBet;
          const onClick = isRoundRobinOrParlay
            ? () => handleSettleBetPressed(record)
            : () => handleEditBetPressed(record);
          return (
            <div className={styles.actionsCell}>
              <Button
                intent="neutral"
                hoverColor="var(--COLOR-PRIMARY)"
                size="small"
                onClick={onClick}
                disabled={disabled}
                data-testid="bets-edit-button"
              >
                <Edit2 size={16} />
              </Button>
            </div>
          );
        },
      },
    ],
    [
      canSettleBet,
      handleBetType,
      handleEditBetPressed,
      handlePayout,
      handleSettleBetPressed,
      handleWager,
      showGamePassColumn,
      taskReferencesByBetId,
      isGamePassSubmissionsLoading,
    ],
  );

  return (
    <div className={styles.sportsBetsContainer}>
      <Modal
        open={isSettleBetModalOpen}
        onCancel={() => setIsSettleBetModalOpen(false)}
        footer={null}
        closable={false}
        width={600}
      >
        <SettleBetPopup
          betData={selectedBetData}
          refreshBets={refreshBets}
          onClose={() => setIsSettleBetModalOpen(false)}
          currencySymbol={currencySymbol}
        />
      </Modal>

      <Modal
        open={isEditBetPopupVisible}
        onCancel={() => setIsEditBetPopupVisible(false)}
        footer={null}
        closable={false}
        width={600}
      >
        <EditBetPopup
          onClose={() => setIsEditBetPopupVisible(false)}
          betData={selectedBetData}
          refreshBets={refreshBets}
          viewedUserId={userData?.user_id}
          currencySymbol={currencySymbol}
        />
      </Modal>

      <div className={styles.sportsBetsHeader}>
        <div className="kpi-cards-container">
          <KpiCard
            isApiRunning={isKpisLoading}
            title={AppStrings.TOTAL_WAGER}
            value={summary.total_wager}
            hugeValue={false}
            percentage={false}
            currencySymbol={currencySymbol}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            isApiRunning={isKpisLoading}
            title={AppStrings.TOTAL_PAYOUT}
            value={summary.total_payout}
            hugeValue={false}
            percentage={false}
            currencySymbol={currencySymbol}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            isApiRunning={isKpisLoading}
            title={AppStrings.NET_REVENUE}
            value={summary.total_net_revenue}
            hugeValue={false}
            percentage={false}
            currencySymbol={currencySymbol}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
        </div>
        <CustomDatePicker
          getData={handleDateChange}
          dates={betsDates}
          setDates={setBetsDates}
          showPresets={true}
          startRange={AppStrings.TODAY}
          allowClearToNull
        />
      </div>

      <div className={styles.sportsBetsTableContainer}>
        <BetsFilters
          statusOptions={statusOptions}
          currencyOptions={currencyOptions}
          betTypeOptions={betTypeOptions}
          status={filters.status}
          moneyType={filters.money_type}
          betTypes={filters.bet_types}
          searchValue={filters.searchValue}
          onStatusChange={handleStatusFilterChange}
          onMoneyTypeChange={handleMoneyTypeFilterChange}
          onBetTypesChange={handleBetTypesFilterChange}
          onSearchValueChange={handleSearchValueChange}
          onSearchPressed={handleSearchPressed}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          hasAppliedOrDraftFilters={
            hasActiveFilters() ||
            appliedStatus.length > 0 ||
            Boolean(appliedMoneyType) ||
            appliedBetTypes.length > 0 ||
            isBetIdSearchActive
          }
        />

        <div
          className={`${styles.tableContainer} ${styles.sportsBetsTable}`}
          style={{ overflowY: "hidden" }}
        >
          <div className={styles.dataRowsContainer}>
            <TypedDataTable
              dataSource={betsToShowInTable}
              columns={columns}
              scrollPreset="compact"
              loading={isApiRunning}
              rowKey={(record) => record?.custom_bet_id}
              expandable={{
                expandedRowKeys,
                onExpand: (expanded, record) => {
                  const key = record.custom_bet_id;
                  if (!key) return;
                  if (expanded) {
                    setExpandedRowKeys([...expandedRowKeys, key]);
                  } else {
                    setExpandedRowKeys(
                      expandedRowKeys.filter((k) => k !== key),
                    );
                  }
                },
              }}
              rowClassName={(record) => {
                if (
                  record.custom_bet_id &&
                  expandedRowKeys.includes(record.custom_bet_id)
                ) {
                  return "expanded-row";
                }
                return record?.isChild ? "child-row" : "parent-row";
              }}
              pagination={
                pagination.total_results > pageSize
                  ? {
                      current: pagination.current_page,
                      // Local state wins over the BE echo: the dropdown
                      // must stay pinned to the admin's selection even if
                      // the BE ignores the `page_size` param (old contract).
                      pageSize,
                      total: pagination.total_results,
                      onChange: (nextPage, nextSize) =>
                        handlePaginationChange(nextPage, nextSize),
                      showSizeChanger: true,
                      pageSizeOptions: [...PAGE_SIZE_OPTIONS],
                      showQuickJumper: false,
                      hideOnSinglePage: true,
                      itemRender: (page, type, originalElement) => {
                        if (type === "page") {
                          const isDisabled =
                            Math.abs(page - pagination.current_page) > 1;
                          return isDisabled ? (
                            <span
                              style={{ opacity: 0.4, pointerEvents: "none" }}
                            >
                              {page}
                            </span>
                          ) : (
                            originalElement
                          );
                        }
                        if (type === "jump-prev" || type === "jump-next")
                          return null;
                        return originalElement;
                      },
                    }
                  : false
              }
            />
          </div>
        </div>

        {isBetIdSearchActive && (
          <div className={styles.resetTableButtonContainer}>
            <Button
              intent="secondary"
              size="large"
              onClick={handleResetSearch}
              data-testid="bets-reset-table"
            >
              {AppStrings.RESET_TABLE}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BetTable;
